import React, { useEffect, useState } from 'react';
import { useService } from '@Client/runner.hooks/useService';
import { SharedAngular } from '@Client/@types/sharedAngular';
import { Guid } from '@Shared.Angular/@types/guid';
import {
  FlowinglyPermissions,
  GovernanceTabs
} from '@Shared.Angular/flowingly.services/flowingly.constants';
import Category from '@Client/runner.directives/common/Category/Category';
import ProcessReviewDueList from './Lists/ProcessReviewDueList/ProcessReviewDueList';
import MyApprovalList from './Lists/ApprovalList/MyApprovalList/MyApprovalList';
import SentForApprovalList from './Lists/ApprovalList/SentForApprovalList/SentForApprovalList';
import AllApprovalList from './Lists/ApprovalList/AllApprovalList/AllApprovalList';
import CustomTabs from '@Shared.Angular/flowingly.components/CustomTabs/CustomTabs';
import Search from '@Shared.Angular/flowingly.components/Search/Search';

type Props = {
  isMobile: boolean;
  backFromState: string;
  onListItemClicked: (id: Guid, loadPublishRequestSchema: boolean) => void;
};

const Governance = (props: Props) => {
  const { isMobile } = props;
  const governanceLoadProcessReviewDueList =
    'governanceLoadProcessReviewDueList';
  const governanceLoadMyApprovalsList = 'governanceLoadMyApprovalsList';
  const governanceSentForApprovalList = 'governanceSentForApprovalList';
  const allCategoriesId = 'all-categories';
  const appConfig = useService<SharedAngular.APP_CONFIG>('APP_CONFIG');
  const flowListManager = useService<FlowListManager>('flowListManager');
  const lodashService = useService<Lodash>('lodashService');
  const pubSubService =
    useService<SharedAngular.PubSubService>('pubsubService');
  const appInsightsService =
    useService<SharedAngular.AppInsightsService>('appInsightsService');
  const runnerCategoryNavigationService =
    useService<RunnerCategoryNavigationService>(
      'runnerCategoryNavigationService'
    );

  const [selectedCategoryId, setSelectedCategoryId] =
    React.useState(allCategoriesId);
  const [categorisedFlowModels, setCategorisedFlowModels] = useState([]);
  const [filteredByCategoryFlowModels, setFilteredByCategoryFlowModels] =
    useState([]);
  const [searchedFlowModels, setSearchedFlowModels] = useState([]);

  const [selectedTab, setSelectedTab] = useState<number | null>(null);
  const [searchPlaceholder, setSearchPlaceholder] = useState('');
  const [searchItem, setSearchItem] = useState('');

  useEffect(() => {
    const governanceSubscriberId = 'flowingly.runner.governance';
    pubSubService.subscribe(
      'SIGNALR_WORKFLOW_NAME_CHANGED',
      (event, params: string) => updateFlowModelName(params),
      governanceSubscriberId
    );
    pubSubService.subscribe(
      'SIGNALR_SETUP_CATEGORY_DELETED',
      refresh,
      governanceSubscriberId
    );
    pubSubService.subscribe(
      'SIGNALR_SETUP_FLOW_MODEL_DELETED',
      refresh,
      governanceSubscriberId
    );
    pubSubService.subscribe(
      'SIGNALR_WORKFLOW_PUBLISHED',
      refresh,
      governanceSubscriberId
    );
    pubSubService.subscribe(
      'SIGNALR_FLOW_MODEL_PUBLISH_REQUESTED',
      refresh,
      governanceSubscriberId
    );
    pubSubService.subscribe(
      'SIGNALR_WORKFLOW_UNPUBLISHED',
      refresh,
      governanceSubscriberId
    );
    return () => {
      pubSubService.unsubscribeAll(governanceSubscriberId);
    };
  }, [selectedTab]);

  useEffect(() => {
    fetchCategorizedFlowModels(selectedTab);
  }, [selectedTab]);

  useEffect(() => {
    if (!categorisedFlowModels) {
      return;
    }
    filterFlowModelsByCategory(selectedCategoryId);
  }, [categorisedFlowModels]);

  useEffect(() => {
    filterAction();
  }, [filteredByCategoryFlowModels, selectedCategoryId, searchItem]);

  const fetchAllApprovalsFlowModels = async () => {
    appInsightsService.startEventTimer(governanceLoadMyApprovalsList);
    const categorisedFlowModels =
      await flowListManager.getCategorizedAllApprovalFlowModels(false);

    setCategorisedFlowModels(
      runnerCategoryNavigationService.getFormattedTreeData(
        categorisedFlowModels
      )
    );
    appInsightsService.trackMetricIfTimerExist(governanceLoadMyApprovalsList);
  };

  const fetchMyApprovalsFlowModels = async () => {
    appInsightsService.startEventTimer(governanceLoadMyApprovalsList);
    const categorisedFlowModels =
      await flowListManager.getCategorizedMyApprovalFlowModels(false);

    setCategorisedFlowModels(
      runnerCategoryNavigationService.getFormattedTreeData(
        categorisedFlowModels
      )
    );
    appInsightsService.trackMetricIfTimerExist(governanceLoadMyApprovalsList);
  };

  const fetchSentForApprovalFlowModels = async () => {
    appInsightsService.startEventTimer(governanceSentForApprovalList);
    const categorisedFlowModels =
      await flowListManager.getCategorizedSentForApprovalFlowModels(false);

    setCategorisedFlowModels(
      runnerCategoryNavigationService.getFormattedTreeData(
        categorisedFlowModels
      )
    );
    appInsightsService.trackMetricIfTimerExist(governanceSentForApprovalList);
  };

  const fetchProcessReviewFlowModels = async () => {
    appInsightsService.startEventTimer(governanceLoadProcessReviewDueList);
    const categorisedFlowModels =
      await flowListManager.getCategorizedProcessOverdueFlowModels(false);

    setCategorisedFlowModels(
      runnerCategoryNavigationService.getFormattedTreeData(
        categorisedFlowModels
      )
    );
    appInsightsService.trackMetricIfTimerExist(
      governanceLoadProcessReviewDueList
    );
  };

  const filterFlowModelsByCategory = (id: string, categoryIndex?: string) => {
    setSelectedCategoryId(id);

    if (id === allCategoriesId) {
      const filteredFlowModels = extractFlowModelsFromArray(
        categorisedFlowModels
      );
      setFilteredByCategoryFlowModels(filteredFlowModels);
    } else {
      const filteredCategories = runnerCategoryNavigationService.findNodeById(
        categorisedFlowModels,
        id
      );
      setFilteredByCategoryFlowModels(filteredCategories.flowModels);
    }
  };

  const extractFlowModelsFromArray = (categories) => {
    let result = [];
    categories.forEach((category) => {
      if (category.flowModels?.length > 0) {
        result = result.concat(category.flowModels);
      }
      if (category.items?.length > 0) {
        result = result.concat(extractFlowModelsFromArray(category.items));
      }
    });

    return result;
  };

  const updateFlowModelName = (jsonParams: string) => {
    if (!Array.isArray(categorisedFlowModels)) {
      return;
    }

    const params = JSON.parse(jsonParams);
    const updatedMap = updateFlowModelNameInCategorisedData(
      categorisedFlowModels,
      params.id,
      params.name
    );
    if (updatedMap) {
      setCategorisedFlowModels([...categorisedFlowModels]);
    }
  };

  const refresh = () => {
    fetchCategorizedFlowModels(selectedTab, selectedCategoryId, searchItem);
  };

  const updateFlowModelNameInCategorisedData = (
    categorisedFlowModels,
    flowModelId: Guid,
    nameToBeUpdated: string
  ) => {
    for (const category of categorisedFlowModels) {
      const map = category.flowModels.find((x) => x.id === flowModelId);
      if (map) {
        map.name = nameToBeUpdated;
        return map;
      }

      if (category.items) {
        const foundFlowModel = updateFlowModelNameInCategorisedData(
          category.items,
          flowModelId,
          nameToBeUpdated
        );
        if (foundFlowModel) {
          return foundFlowModel;
        }
      }
    }
    return undefined;
  };

  const handleSearchChange = (value: string) => setSearchItem(value);

  const executeAllApprovalsFilter = () =>
    executeFilter(['description', 'name', 'author'], 'dueBy');
  const executeMyApprovalsFilter = () =>
    executeFilter(['description', 'name', 'author'], 'dueBy');
  const executeSentForApprovalFilter = () =>
    executeFilter(['description', 'name', 'approver'], 'dueBy');
  const executeProcessReviewDueFilter = () =>
    executeFilter(['description', 'name'], 'processOverdueOn');

  const executeFilter = (searchFields: string[], sortBy: string) => {
    const filteredResults = filteredByCategoryFlowModels.filter((x) =>
      searchFields.some((field) =>
        x[field]?.toLowerCase().includes(searchItem.toLowerCase())
      )
    );
    const ordered = lodashService.orderBy(filteredResults, [sortBy], ['asc']);
    setSearchedFlowModels(ordered);
  };

  const permissionsService =
    useService<SharedAngular.PermissionsService>('permissionsService');
  const isApprover = permissionsService.currentUserHasPermission(
    FlowinglyPermissions.GOVERNANCE_FLOWMODEL_PUBLISHER_CANDIDATE
  );
  const hasAllApprovalAccess = permissionsService.currentUserHasPermission(
    FlowinglyPermissions.GOVERNANCE_FLOWMODEL_PUBLISH_ALL_ACCESS
  );
  const hasPublishPermission =
    permissionsService.currentUserHasAnyOfPermissions([
      FlowinglyPermissions.FLOWMODEL_PUBLISH,
      FlowinglyPermissions.FLOWMODEL_PUBLISH_OWN
    ]);

  const isEditor =
    permissionsService.currentUserHasPermission(
      FlowinglyPermissions.GOVERNANCE_ACCESS
    ) &&
    !isApprover &&
    !hasAllApprovalAccess &&
    !hasPublishPermission;

  const flowModelApiService = useService<SharedAngular.FlowModelApiService>(
    'flowModelApiService'
  );
  const [hasSentForApprovalData, setHasSentForApprovalData] = useState(false);
  useEffect(() => {
    (async () => {
      try {
        const data = await flowModelApiService.getSentForApprovalFlowModels(
          false
        );
        setHasSentForApprovalData(!!data?.length);
      } catch (error) {
        setHasSentForApprovalData(false);
      } finally {
        setInitialized(true);
      }
    })();
  }, []);

  const hideSentForApprovalTab = !isEditor && !hasSentForApprovalData;

  const tabs = [
    {
      key: GovernanceTabs.ALL_APPROVALS,
      label: 'All publish approvals',
      isHidden: !hasAllApprovalAccess,
      fetch: async () => {
        setSearchPlaceholder(
          'Search by flow model name, description or author'
        );
        await fetchAllApprovalsFlowModels();
      },
      filter: () => executeAllApprovalsFilter(),
      render: (listProps) => <AllApprovalList {...listProps} />
    },
    {
      key: GovernanceTabs.MY_APPROVALS,
      label: 'My publish approvals',
      isHidden: !isApprover,
      fetch: async () => {
        setSearchPlaceholder(
          'Search by flow model name, description or author'
        );
        await fetchMyApprovalsFlowModels();
      },
      filter: () => executeMyApprovalsFilter(),
      render: (listProps) => <MyApprovalList {...listProps} />
    },
    {
      key: GovernanceTabs.SENT_FOR_APPROVAL,
      label: 'Sent for publish approval',
      isHidden: hideSentForApprovalTab,
      fetch: async () => {
        setSearchPlaceholder(
          'Search by flow model name, description or approver'
        );
        await fetchSentForApprovalFlowModels();
      },
      filter: () => executeSentForApprovalFilter(),
      render: (listProps) => <SentForApprovalList {...listProps} />
    },
    {
      key: GovernanceTabs.PROCESS_REVIEW_DUE,
      label: 'Process review due',
      fetch: async () => {
        setSearchPlaceholder('Search by flow model name or description');
        await fetchProcessReviewFlowModels();
      },
      filter: () => executeProcessReviewDueFilter(),
      render: (listProps) => <ProcessReviewDueList {...listProps} />
    }
  ];

  const fetchCategorizedFlowModels = async (
    tabIndex: number,
    category = null,
    searchText = null
  ) => {
    if (category == null) {
      setSelectedCategoryId(allCategoriesId);
    }
    if (searchText == null) {
      setSearchItem('');
    }

    await tabs[tabIndex]?.fetch();
  };

  const filterAction = () => {
    if (tabs[selectedTab]?.filter) {
      tabs[selectedTab].filter();
    }
  };

  const renderSelectedList = () => {
    const listProps = {
      isMobile,
      filteredFlowModels: searchedFlowModels,
      selectedTab,
      appConfig
    };

    return tabs[selectedTab]?.render
      ? tabs[selectedTab].render(listProps)
      : null;
  };

  const [initialized, setInitialized] = useState(false);
  useEffect(() => {
    if (initialized && selectedTab === null) {
      const firstVisibleTabIndex = tabs.findIndex((tab) => !tab.isHidden);
      if (firstVisibleTabIndex >= 0) {
        setSelectedTab(firstVisibleTabIndex);
      }
    }
  }, [initialized, selectedTab]);

  const handleTabChange = (
    _event: React.SyntheticEvent,
    newValue: React.SetStateAction<number>
  ) => {
    setSelectedTab(newValue);
  };

  return (
    <div className="categories-row row" id="governance">
      <Category
        categorisedItems={categorisedFlowModels}
        selectedCategoryId={selectedCategoryId}
        allCategoriesId={allCategoriesId}
        isMobile={isMobile}
        onCategoryClick={filterFlowModelsByCategory}
      />
      <div className={isMobile ? '' : 'cards-col col mt-20'}>
        <div className={`start-category-list ${isMobile ? 'isMobile' : ''}`}>
          <div className="governance-search-tabs-container">
            <Search
              placeholder={searchPlaceholder}
              value={searchItem}
              onValueChange={handleSearchChange}
            />
            <CustomTabs
              selectedTabIndex={selectedTab}
              onChange={handleTabChange}
              tabs={tabs}
            ></CustomTabs>
          </div>
          {renderSelectedList()}
        </div>
      </div>
    </div>
  );
};

export default Governance;
